import { LitElement, createRef, html, ref, unsafeHTML } from '@hh/global.js'

class ProductBisModal extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    translationStrings: {
      type: String,
      attribute: 'translation-strings',
    },
    klaviyoId: {
      type: String,
      attribute: 'klaviyo-account-id',
    },
    newletterId: {
      type: String,
      attribute: 'newsletter-list',
    },
    marketingConsentDefaultOptIn: {
      type: Boolean,
      attribute: 'marketing-consent-default-opt-in',
    },
    _state: {
      type: String,
      state: true,
    },
    _productData: {
      type: Object,
    },
  }

  formRef = createRef()

  createRenderRoot() {
    return this
  }

  constructor() {
    super()
    this._state = 'idle'
    this.open = false
    this.openTimeout = null
    this._selectedVariantId = ''
    this._openRequestId = 0
  }

  connectedCallback() {
    super.connectedCallback()

    const decodedTranslation = decodeURIComponent(
      this.translationStrings.replace(/\+/g, '%20') || ''
    ).replace(/&#39;/g, "'")
    this.translationObject = JSON.parse(decodedTranslation)

    document.addEventListener('click', this.handleProductBisTrigger.bind(this))
  }

  updated(changedProperties) {
    clearTimeout(this.openTimeout)

    if (changedProperties.has('open')) {
      this.openTimeout = setTimeout(() => {
        if (this.open) {
          document.body.classList.add('overflow-hidden')
        } else if (!this.open) {
          document.body.classList.remove('overflow-hidden')
        }
      }, 10)
    }
  }

  handleProductBisTrigger(e) {
    const trigger = e.target?.closest('.js-product-bis-trigger')
    if (!trigger) return

    const handle = trigger.dataset?.productHandle
    if (!handle) return

    this.openProductBisModal(handle, trigger.dataset?.variantId || '')
  }

  async openProductBisModal(handle, variantId = '') {
    const requestId = ++this._openRequestId

    this._state = 'loading'
    this._selectedVariantId = variantId
    this._productData = null
    this.open = true

    const productData = await this.fetchProductDetails(handle)
    if (requestId !== this._openRequestId) return

    this._productData = productData
    this._state = productData ? 'loaded' : 'error'
  }

  closeModal() {
    this._openRequestId += 1
    this.open = false
    const form = this.formRef.value
    form?.reset()
  }

  async fetchProductDetails(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`)
      return await res.json()
    } catch (error) {
      console.error(error)
      return null
    }
  }

  async submitBackInStock(e) {
    e.preventDefault()
    if (this._state !== 'loaded') return

    const formData = new FormData(e.target)
    if (!formData.get('email') || !formData.get('variant')) return

    try {
      const BISResponse = await this.addToBISList(formData)
      this._state = (BISResponse === true) ? 'success' : 'error'
    } catch (error) {
      console.error(error)
      this._state = 'error'
    }
  }

  async addToBISList(formData) {
    const bisUrl = 'https://a.klaviyo.com/client/back-in-stock-subscriptions'
    const subscribeURL = 'https://a.klaviyo.com/client/subscriptions'

    const urlParams = new URLSearchParams(formData)
    const apiKey = urlParams.get('a')
    const email = urlParams.get('email')
    const subscribe = urlParams.get('subscribe_for_newsletter')
    const list = urlParams.get('g')
    const variantId = urlParams.get('variant')

    try {
      const bisRes = await fetch(`${bisUrl}/?company_id=${apiKey}`, {
        headers: {
          accept: 'application/json',
          revision: '2024-06-15',
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  }
                }
              }
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`
                }
              }
            }
          }
        }),
        method: 'POST',
      })

      if (!bisRes.ok) {
        throw new Error(bisRes?.statusText)
      }

      if (subscribe === 'on') {
        const subscribeRes = await fetch(`${subscribeURL}/?company_id=${apiKey}`, {
          headers: {
            accept: 'application/json',
            revision: '2024-06-15',
            'content-type': 'application/json'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email
                    }
                  }
                }
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: list
                  }
                }
              }
            }
          }),
          method: 'POST',
        })

        if (!subscribeRes.ok) {
          throw new Error(subscribeRes?.statusText)
        }
      }

      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  getNewsletterForm() {
    if (!this.newletterId) return ''

    return html`
      <input type="hidden" name="g" value="${this.newletterId}" />
      <div class="product-bis-modal__field product-bis-modal__checkbox-field">
        <input
          type="checkbox"
          id="product-bis-modal-newsletter-checkbox"
          class="product-bis-modal__checkbox"
          name="subscribe_for_newsletter"
          ?checked=${this.marketingConsentDefaultOptIn}
        />
        <label
          for="product-bis-modal-newsletter-checkbox"
          class="product-bis-modal__newsletter-checkbox-label"
        >
          ${unsafeHTML(this.translationObject['newsletter_subscribe_label'])}
        </label>
      </div>
    `
  }

  getFormElement() {
    const productId = this._productData?.id || ''
    const variants = this._productData?.variants || []
    const variantId =
      this._selectedVariantId ||
      variants.find((variant) => !variant.available)?.id ||
      variants[0]?.id ||
      ''

    if (this._state === 'success') {
      return html`<div class="product-bis-modal__success-message">
        ${unsafeHTML(this.translationObject['subscription_success_label'])}
      </div>`
    } else if (this._state === 'error') {
      return html`<div class="product-bis-modal__error-message">
        ${unsafeHTML(this.translationObject['subscription_error_label'])}
      </div>`
    }

    return html`<p class="product-bis-modal__message">
        ${this.translationObject['body_content']}
      </p>

      <form
        method="post"
        @submit="${this.submitBackInStock}"
        ${ref(this.formRef)}
      >
        <input type="hidden" name="platform" value="shopify" />
        <input type="hidden" name="a" value="${this.klaviyoId}" />
        <input type="hidden" name="product" value="${productId}" />
        <input type="hidden" name="variant" value="${variantId}" />

        <div class="product-bis-modal__email-field">
          <input
            type="email"
            name="email"
            class="product-bis-modal__input-field"
            placeholder="Email Address"
            required
            ?disabled=${this._state !== 'loaded'}
          />
        </div>
        ${this.getNewsletterForm()}
        <button
          type="submit"
          class="button button--action button--full center"
          ?disabled=${this._state !== 'loaded' || !variantId}
        >
          <span>${this.translationObject['button_label']}</span>
        </button>
      </form>`
  }

  render() {
    const productTitle = this._productData?.title || ''

    return html`<div class="product-bis-modal" aria-modal="true">
      <div
        class="product-bis-modal__overlay"
        aria-hidden="true"
        @click="${this.closeModal}"
      ></div>
      <hh-focus-trap active="${this.open}">
        <div class="product-bis-modal__panel" role="document">
          <button
            type="button"
            class="button product-bis-modal__close"
            aria-label="Close"
            @click="${this.closeModal}"
          >
            ×
          </button>

          <div class="h5 product_bis-modal__title">${productTitle}</div>

          ${this.getFormElement()}
        </div>
      </hh-focus-trap>
    </div>`
  }
}

customElements.define('product-bis-modal', ProductBisModal)
